<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import NavigationPanelMenuItem from './NavigationPanelMenuItem.vue';
import UserBarDropdownItem from '../../parts/user-bar/UserBarDropdownItem.vue';
import { useAppStateStore } from '../../../stores/app-state-store';
import { useUserStore } from '../../../stores/user-store';

const appStateStore = useAppStateStore();
const userStore = useUserStore();

// Editor-managed navigation, hydrated once per page load from PHP.
const navigationItems = window.gsdNowAppData?.navigation || [];

const visibleNavigationItems = computed(() =>
  navigationItems.filter(item => {
    if (item.requiredFeature && !userStore.canAccessFeature(item.requiredFeature)) {
      return false;
    }

    if (!item.guestVisible && userStore.isGuest) {
      return false;
    }

    return true;
  })
);

const normalizePath = path => path.replace(/\/+$/, '') || '/';

const isNavItemActive = item => {
  const url = item.url || '';

  // External links are never the active page.
  if (/^https?:\/\//i.test(url) && !url.startsWith(window.location.origin)) {
    return false;
  }

  const current = normalizePath(window.location.pathname);
  const target = normalizePath(url);

  return current === target || current.startsWith(`${target}/`);
};

const isToggled = ref(false);

const toggleMenu = () => {
  isToggled.value = !isToggled.value;
};

defineProps({
  isInModal: {
    type: Boolean,
    default: false,
  },
});

const breakpointLg = 1000;

const handleResize = () => {
  if (window.innerWidth >= breakpointLg) {
    isToggled.value = false;
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <nav id="nav-bar" :class="{ 'in-modal': isInModal }">
    <div class="navigation-panel-container">
      <div class="navigation-panel-header">
        <h2><a href="/">GSD Now</a></h2>

        <button class="hamburger-btn-container" @click="toggleMenu">
          <img
            v-if="!isToggled"
            class="nav-bar-hamburger-icon"
            src="@/images/icon-hamburger.svg"
            alt="hamburger icon"
          />
          <img
            v-if="isToggled"
            class="nav-bar-hamburger-close-icon"
            src="@/images/icon-hamburger-close.svg"
            alt="hamburger close icon"
          />
        </button>
      </div>

      <ul :class="['nav-bar-menu', { toggled: isToggled }]">
        <NavigationPanelMenuItem
          v-for="(item, index) in visibleNavigationItems"
          :key="index"
          :is-active="isNavItemActive(item)"
          :menu-item-label="item.label"
          :menu-item-url="item.url"
        />

        <UserBarDropdownItem
          v-if="isToggled && !userStore.isGuest"
          class="user-dropdown"
          menu-item-label="Profile &amp; Settings"
          menu-item-location="/profile"
        />

        <UserBarDropdownItem
          v-if="isToggled"
          menu-item-label="Sign out"
          :menu-item-location="appStateStore.logoutUrl"
        />
      </ul>
    </div>
  </nav>
</template>
